/**
 * Error tracking service for production monitoring
 * Centralizes error handling and tracking
 */

import Logger from './logger';
import secureDataStorageService from './secureDataStorageService';

export interface ErrorContext {
  userId?: string;
  component?: string;
  action?: string;
  data?: any;
  [key: string]: any; // Allow additional properties
}

class ErrorTracker {
  /**
   * Track errors with context
   * Enhanced with security monitoring integration
   */
  static trackError(error: Error, context: string, errorContext?: ErrorContext): void {
    // Log to console with proper formatting
    Logger.error(`Error in ${context}`, error, errorContext);
    
    // Future Enhancement: Integrate with error tracking service (Sentry, Bugsnag, etc.)
    // This is a placeholder for future production error tracking integration
    // Currently, errors are logged via Logger service which sends to Cloud Logging
    // this.sendToErrorService(error, context, errorContext);
  }
  
  /**
   * Track user actions for analytics
   */
  static trackUserAction(action: string, userId: string, data?: any): void {
    Logger.info(`User action: ${action}`, { userId, data });
    
    // Future Enhancement: Integrate with analytics service (Google Analytics, Mixpanel, etc.)
    // This is a placeholder for future analytics integration
    // this.sendToAnalytics(action, userId, data);
  }
  
  /**
   * Track performance metrics
   */
  static trackPerformance(operation: string, duration: number, context?: any): void {
    Logger.info(`Performance: ${operation} took ${duration}ms`, context);
    
    // Note: Performance monitoring is partially implemented via sendToPerformanceService
    // Future Enhancement: Integrate with dedicated performance monitoring service
    // this.sendToPerformanceService(operation, duration, context);
  }
  
  /**
   * Track business events
   */
  static trackBusinessEvent(event: string, data: any): void {
    Logger.info(`Business event: ${event}`, data);
    
    // Future Enhancement: Integrate with business analytics service
    // This is a placeholder for future business intelligence integration
    // this.sendToBusinessAnalytics(event, data);
  }

  /**
   * Track data protection events
   */
  static trackDataProtectionEvent(event: string, classification: string, userId?: string, data?: any): void {
    Logger.info(`Data protection event: ${event}`, { classification, userId, data });
    
    // Log to secure data storage service for audit trail
    try {
      secureDataStorageService.logAuditEntry({
        operation: 'store',
        classification: classification as any,
        userId,
        success: true,
        metadata: { event, data }
      });
    } catch (error) {
      Logger.error('Failed to log data protection event', error instanceof Error ? error : new Error('Unknown error'));
    }
  }
  
  /**
   * Handle API errors
   */
  static handleApiError(error: Error, endpoint: string, context?: ErrorContext): void {
    this.trackError(error, `API Error: ${endpoint}`, context);
  }
  
  /**
   * Handle database errors
   */
  static handleDatabaseError(error: Error, operation: string, context?: ErrorContext): void {
    this.trackError(error, `Database Error: ${operation}`, context);
  }
  
  /**
   * Handle validation errors
   */
  static handleValidationError(error: Error, field: string, context?: ErrorContext): void {
    this.trackError(error, `Validation Error: ${field}`, context);
  }

  /**
   * Handle network errors with retry tracking
   */
  static handleNetworkError(error: Error, operation: string, retryCount: number, context?: ErrorContext): void {
    this.trackError(error, `Network Error: ${operation}`, {
      ...context,
      retryCount,
      errorType: 'network'
    });
  }

  /**
   * Handle timeout errors
   */
  static handleTimeoutError(error: Error, operation: string, timeout: number, context?: ErrorContext): void {
    this.trackError(error, `Timeout Error: ${operation}`, {
      ...context,
      timeout,
      errorType: 'timeout'
    });
  }

  /**
   * Handle retry exhaustion
   */
  static handleRetryExhaustion(error: Error, operation: string, maxRetries: number, context?: ErrorContext): void {
    this.trackError(error, `Retry Exhaustion: ${operation}`, {
      ...context,
      maxRetries,
      errorType: 'retry_exhaustion'
    });
  }

  /**
   * Handle circuit breaker errors
   */
  static handleCircuitBreakerError(operation: string, context?: ErrorContext): void {
    const error = new Error('Circuit breaker is open');
    this.trackError(error, `Circuit Breaker: ${operation}`, {
      ...context,
      errorType: 'circuit_breaker'
    });
  }

  /**
   * Track API performance
   */
  static trackApiPerformance(endpoint: string, duration: number, status: number, retryCount: number = 0): void {
    this.trackPerformance(`API: ${endpoint}`, duration, {
      endpoint,
      status,
      retryCount,
      type: 'api'
    });
  }

  /**
   * Track Firebase performance
   */
  static trackFirebasePerformance(operation: string, duration: number, collection: string, retryCount: number = 0): void {
    this.trackPerformance(`Firebase: ${operation}`, duration, {
      operation,
      collection,
      retryCount,
      type: 'firebase'
    });
  }

  /**
   * Track error recovery
   */
  static trackErrorRecovery(operation: string, retryCount: number, context?: ErrorContext): void {
    Logger.info(`Error recovery successful: ${operation}`, {
      ...context,
      retryCount,
      type: 'recovery'
    });
  }
  
  /**
   * Send to external error service (placeholder)
   */
  private static sendToErrorService(error: Error, context: string, errorContext?: ErrorContext): void {
    // Future Enhancement: Implement Sentry, Bugsnag, or other error tracking service
    // Currently, errors are logged via Logger service which sends to Cloud Logging
    // Example:
    // Sentry.captureException(error, {
    //   tags: { context },
    //   user: { id: errorContext?.userId },
    //   extra: errorContext
    // });
  }
  

  /**
   * Send to analytics service (placeholder)
   */
  private static sendToAnalytics(action: string, userId: string, data?: any): void {
    // Future Enhancement: Implement Google Analytics, Mixpanel, or other analytics service
    // This is a placeholder for future analytics integration
  }
  
  /**
   * Send to performance monitoring (placeholder)
   */
  private static sendToPerformanceService(operation: string, duration: number, context?: any): void {
    try {
      // Import and use the enhanced performance metrics service
      import('./enhancedPerformanceMetrics').then(({ default: enhancedPerformanceMetrics }) => {
        enhancedPerformanceMetrics.recordMetric({
          id: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          operation,
          duration,
          timestamp: Date.now(),
          success: false,
          error: context?.error?.message || 'Unknown error',
          userAgent: navigator.userAgent,
          context: {
            component: context?.component,
            route: window.location.pathname,
            action: context?.action,
            data: context
          }
        });
      }).catch(() => {
        // Silently fail if performance service is not available
      });
    } catch (error) {
      // Silently fail to avoid infinite loops
    }
  }
  
  /**
   * Send to business analytics (placeholder)
   */
  private static sendToBusinessAnalytics(event: string, data: any): void {
    // Future Enhancement: Implement business analytics service
    // This is a placeholder for future business intelligence integration
  }
}

export default ErrorTracker;
